import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardLayout from './components/layout/DashboardLayout';
import { ProtectedRoute as PermissionProtectedRoute } from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ui/ErrorBoundary';
import GlobalLoadingIndicator from './components/ui/GlobalLoadingIndicator';
import { useAuthStore } from './store/auth';
import { canAccessAdminDashboard, normalizeUserRoles } from './utils/permissions';
import './styles/globals.css';
import CountriesPage from './pages/countries';

// Lazy load components for code splitting
const AuthPage = React.lazy(() => import('./pages/auth'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/privacy-policy'));
const TeamsPage = React.lazy(() => import('./pages/teams'));
const TeamDetailPage = React.lazy(() => import('./pages/teams/detail'));
const PlayersPage = React.lazy(() => import('./pages/players'));
const PlayerDetailPage = React.lazy(() => import('./pages/players/detail'));
const SportTypesPage = React.lazy(() => import('./pages/sportTypes'));
const SportTypeDetailPage = React.lazy(() => import('./pages/sportTypes/detail'));
const SportClubsPage = React.lazy(() => import('./pages/sportClubs'));
const SportClubDetailPage = React.lazy(() => import('./pages/sportClubs/detail'));
const CitiesPage = React.lazy(() => import('./pages/cities'));
const CityDetailPage = React.lazy(() => import('./pages/cities/detail'));
const TournamentsPage = React.lazy(() => import('./pages/tournaments'));
const TournamentDetailPage = React.lazy(() => import('./pages/tournaments/detail'));
const TournamentCategoriesPage = React.lazy(() => import('./pages/tournamentCategories'));
const TournamentCategoryDetailPage = React.lazy(() => import('./pages/tournamentCategories/detail'));
const MatchesPage = React.lazy(() => import('./pages/matches'));
const MatchDetailPage = React.lazy(() => import('./pages/matches/detail'));
const MatchParticipantsPage = React.lazy(() => import('./pages/matchParticipants'));
const MatchParticipantsOverviewPage = React.lazy(() => import('./pages/matchParticipants/overview'));
const PermissionsPage = React.lazy(() => import('./pages/permissions'));
const AchievementsPage = React.lazy(() => import('./pages/achievements'));
const AchievementDetailPage = React.lazy(() => import('./pages/achievements/detail'));
const RegionsPage = React.lazy(() => import('./pages/regions'));
const PlaygroundsPage = React.lazy(() => import('./pages/playgrounds'));
const PlaygroundDetailPage = React.lazy(() => import('./pages/playgrounds/detail'));
const UsersPage = React.lazy(() => import('./pages/users'));
const UserDetailPage = React.lazy(() => import('./pages/users/detail'));
const FilesPage = React.lazy(() => import('./pages/files'));
const NewsPage = React.lazy(() => import('./pages/news'));
const FavoritesPage = React.lazy(() => import('./pages/favorites'));
const LeagueAdminsPage = React.lazy(() => import('./pages/leagueAdmins'));
const MyLeaguePage = React.lazy(() => import('./pages/myLeague'));
const MobileApp = React.lazy(() => import('./mobile/app/App'));

// Loading component for lazy-loaded routes
const RouteLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
  </div>
);

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, bootstrapped } = useAuthStore();
  const hasDashboardAccess = canAccessAdminDashboard(normalizeUserRoles(user));

  if (!bootstrapped) {
    return <RouteLoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/dashboard/auth" replace />;
  }

  if (!hasDashboardAccess) {
    return <Navigate to="/dashboard/auth" replace />;
  }

  return <>{children}</>;
};

const DashboardIndexRedirect: React.FC = () => {
  const { user } = useAuthStore();
  const roles = normalizeUserRoles(user);
  const isLeagueAdmin = roles.some((role) => role.name === 'ADMIN_LEAGUE');
  return <Navigate to={isLeagueAdmin ? '/dashboard/my-league' : '/dashboard/teams'} replace />;
};

const App: React.FC = () => {
  const bootstrapFromStoredToken = useAuthStore((state) => state.bootstrapFromStoredToken);

  useEffect(() => {
    void bootstrapFromStoredToken();
  }, [bootstrapFromStoredToken]);

  return (
    <ErrorBoundary>
      <GlobalLoadingIndicator />
      <Routes>
          {/* Public routes */}
          <Route
            path="/privacy-policy"
            element={
              <Suspense fallback={<RouteLoadingSpinner />}>
                <ErrorBoundary>
                  <PrivacyPolicyPage />
                </ErrorBoundary>
              </Suspense>
            }
          />

          <Route
            path="/dashboard/auth"
            element={
              <Suspense fallback={<RouteLoadingSpinner />}>
                <ErrorBoundary>
                  <AuthPage />
                </ErrorBoundary>
              </Suspense>
            }
          />

          <Route
            path="/*"
            element={
              <Suspense fallback={<RouteLoadingSpinner />}>
                <MobileApp />
              </Suspense>
            }
          />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardIndexRedirect />} />
            <Route
              path="teams"
              element={
                <PermissionProtectedRoute permission="teams.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <TeamsPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="teams/:id"
              element={
                <PermissionProtectedRoute permission="teams.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <TeamDetailPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="players"
              element={
                <PermissionProtectedRoute permission="players.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <PlayersPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="players/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <PlayerDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="users"
              element={
                <PermissionProtectedRoute permission="users.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <UsersPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="users/:id"
              element={
                <PermissionProtectedRoute permission="users.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <UserDetailPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="sport-types"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <SportTypesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="sport-types/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <SportTypeDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="sport-clubs"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <SportClubsPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="sport-clubs/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <SportClubDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="cities"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <CitiesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="cities/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <CityDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="tournaments"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <TournamentsPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="tournaments/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <TournamentDetailPage />
                </Suspense>
              }
            />

            <Route
              path="tournament-categories"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <TournamentCategoriesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="tournament-categories/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <TournamentCategoryDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="matches"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <MatchesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="matches/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <MatchDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="match-participants"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <MatchParticipantsOverviewPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="match-participants/:matchId"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <MatchParticipantsPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="permissions"
              element={
                <PermissionProtectedRoute permission="permissions.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <PermissionsPage />
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="countries"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <CountriesPage />
                </Suspense>
              }
            />

            <Route
              path="achievements"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <AchievementsPage />
                </Suspense>
              }
            />

            <Route
              path="achievements/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <AchievementDetailPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="regions"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <RegionsPage />
                </Suspense>
              }
            />

            <Route
              path="playgrounds"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <PlaygroundsPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="playgrounds/:id"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <PlaygroundDetailPage />
                  </ErrorBoundary>
                </Suspense>
              } />

            <Route
              path="files"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <FilesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="news"
              element={
                <PermissionProtectedRoute permission="news.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <NewsPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="favorites"
              element={
                <Suspense fallback={<RouteLoadingSpinner />}>
                  <ErrorBoundary>
                    <FavoritesPage />
                  </ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path="league-admins"
              element={
                <PermissionProtectedRoute permission="league-admins.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <LeagueAdminsPage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />

            <Route
              path="my-league"
              element={
                <PermissionProtectedRoute permission="my-league.view">
                  <Suspense fallback={<RouteLoadingSpinner />}>
                    <ErrorBoundary>
                      <MyLeaguePage />
                    </ErrorBoundary>
                  </Suspense>
                </PermissionProtectedRoute>
              }
            />
          </Route>
      </Routes>
    </ErrorBoundary>
  );
};

export default App;
